import { create } from 'zustand';
import { aiIntelligenceService } from '../services/ai-intelligence-service';
import type {
  AiIntelligenceTab,
  QuoteSummary,
  ModelSummary,
  SimulationParams,
  SimulationResult,
  JustificationParams,
  JustificationResult,
  PricingSuggestionsResult,
  ApprovalScoreResult,
  BusinessInsightsResult,
  ModelAnalysisResult,
} from '../types/ai-intelligence.types';

interface AiIntelligenceState {
  // Tab activo
  activeTab: AiIntelligenceTab;

  // Datos de selectores
  quotes: QuoteSummary[];
  models: ModelSummary[];
  isLoadingSelectors: boolean;

  // Simulación
  simulationResult: SimulationResult | null;
  isSimulating: boolean;
  simulationError: string | null;

  // Approval Score
  approvalScore: ApprovalScoreResult | null;
  isLoadingScore: boolean;
  scoreError: string | null;

  // Sugerencias
  suggestions: PricingSuggestionsResult | null;
  isLoadingSuggestions: boolean;
  suggestionsError: string | null;

  // Justificación
  justification: JustificationResult | null;
  isLoadingJustification: boolean;
  justificationError: string | null;

  // Insights
  insights: BusinessInsightsResult | null;
  isLoadingInsights: boolean;
  insightsError: string | null;

  // Model Analysis
  modelAnalysis: ModelAnalysisResult | null;
  isLoadingModelAnalysis: boolean;
  modelAnalysisError: string | null;

  // Acciones
  setActiveTab: (tab: AiIntelligenceTab) => void;
  fetchSelectors: () => Promise<void>;
  simulate: (params: SimulationParams) => Promise<void>;
  fetchApprovalScore: (quoteId: string) => Promise<void>;
  fetchSuggestions: (params: {
    quoteId?: string;
    modelId?: string;
    clientName?: string;
  }) => Promise<void>;
  fetchJustification: (params: JustificationParams) => Promise<void>;
  fetchInsights: (period?: string, focus?: string) => Promise<void>;
  fetchModelAnalysis: (modelId: string) => Promise<void>;
  clearSimulation: () => void;
  clearScore: () => void;
  clearSuggestions: () => void;
  clearJustification: () => void;
}

export const useAiIntelligenceStore = create<AiIntelligenceState>((set) => ({
  activeTab: 'simulator',
  quotes: [],
  models: [],
  isLoadingSelectors: false,
  simulationResult: null,
  isSimulating: false,
  simulationError: null,
  approvalScore: null,
  isLoadingScore: false,
  scoreError: null,
  suggestions: null,
  isLoadingSuggestions: false,
  suggestionsError: null,
  justification: null,
  isLoadingJustification: false,
  justificationError: null,
  insights: null,
  isLoadingInsights: false,
  insightsError: null,
  modelAnalysis: null,
  isLoadingModelAnalysis: false,
  modelAnalysisError: null,

  setActiveTab: (tab) => set({ activeTab: tab }),

  fetchSelectors: async () => {
    set({ isLoadingSelectors: true });
    try {
      const [quotes, models] = await Promise.all([
        aiIntelligenceService.getQuotesSummary(),
        aiIntelligenceService.getModelsSummary(),
      ]);
      console.log('[AI Intelligence] Selectors loaded:', {
        quotes: quotes.length,
        models: models.length,
      });
      set({ quotes, models, isLoadingSelectors: false });
    } catch (err) {
      console.error('[AI Intelligence] Error fetching selectors:', err);
      set({ isLoadingSelectors: false });
    }
  },

  simulate: async (params) => {
    set({ isSimulating: true, simulationError: null, simulationResult: null });
    try {
      const result = await aiIntelligenceService.simulate(params);
      set({ simulationResult: result, isSimulating: false });
    } catch (err: any) {
      set({
        simulationError: err?.response?.data?.error || 'Error al simular',
        isSimulating: false,
      });
    }
  },

  fetchApprovalScore: async (quoteId) => {
    set({ isLoadingScore: true, scoreError: null, approvalScore: null });
    try {
      const result = await aiIntelligenceService.getApprovalScore(quoteId);
      set({ approvalScore: result, isLoadingScore: false });
    } catch (err: any) {
      set({
        scoreError: err?.response?.data?.error || 'Error al calcular score',
        isLoadingScore: false,
      });
    }
  },

  fetchSuggestions: async (params) => {
    set({ isLoadingSuggestions: true, suggestionsError: null, suggestions: null });
    try {
      const result = await aiIntelligenceService.getSuggestions(params);
      set({ suggestions: result, isLoadingSuggestions: false });
    } catch (err: any) {
      set({
        suggestionsError: err?.response?.data?.error || 'Error al obtener sugerencias',
        isLoadingSuggestions: false,
      });
    }
  },

  fetchJustification: async (params) => {
    set({ isLoadingJustification: true, justificationError: null, justification: null });
    try {
      const result = await aiIntelligenceService.getJustification(params);
      set({ justification: result, isLoadingJustification: false });
    } catch (err: any) {
      set({
        justificationError: err?.response?.data?.error || 'Error al generar justificación',
        isLoadingJustification: false,
      });
    }
  },

  fetchInsights: async (period, focus) => {
    set({ isLoadingInsights: true, insightsError: null, insights: null });
    try {
      const result = await aiIntelligenceService.getInsights(period, focus);
      set({ insights: result, isLoadingInsights: false });
    } catch (err: any) {
      set({
        insightsError: err?.response?.data?.error || 'Error al generar insights',
        isLoadingInsights: false,
      });
    }
  },

  fetchModelAnalysis: async (modelId) => {
    set({ isLoadingModelAnalysis: true, modelAnalysisError: null, modelAnalysis: null });
    try {
      const result = await aiIntelligenceService.getModelAnalysis(modelId);
      set({ modelAnalysis: result, isLoadingModelAnalysis: false });
    } catch (err: any) {
      set({
        modelAnalysisError: err?.response?.data?.error || 'Error al analizar modelo',
        isLoadingModelAnalysis: false,
      });
    }
  },

  clearSimulation: () => set({ simulationResult: null, simulationError: null }),
  clearScore: () => set({ approvalScore: null, scoreError: null }),
  clearSuggestions: () => set({ suggestions: null, suggestionsError: null }),
  clearJustification: () => set({ justification: null, justificationError: null }),
}));
